import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDevice } from '../../context/DeviceContext';
import { V4_TYPE3_OFFSETS } from '../../utils/PerformanceType3';
import {
  SENSOR_TYPE_3950,
  extractLodFromSensorFlag,
  getLodMillimeters,
  getLodOptions,
  isLodOptionActive,
  normalizeLodWriteValue,
} from '../../utils/SensorLod';
import { toast } from '../Toast';
import {
  resolveAutoApplyStatus,
  useAutoApply,
} from '../../utils/useAutoApply';
import './AdvancedSettings.css';

// 传感器性能模式：fullSpeedEnable(byte7) 与 fullbloodEnable(byte8) 互斥，
// 两者都为 0 即关闭。字段号是 payload 偏移，完整包偏移统一 +4。
const SENSOR_MODE_OFF = 'off';
const SENSOR_MODE_FULLSPEED = 'fullspeed';
const SENSOR_MODE_FULLBLOOD = 'fullblood';

const SENSOR_MODE_OPTIONS = [
  { value: SENSOR_MODE_OFF, label: '关闭' },
  { value: SENSOR_MODE_FULLSPEED, label: '电竞模式' },
  { value: SENSOR_MODE_FULLBLOOD, label: '满血模式' },
];

const SENSOR_MODE_DESC = {
  [SENSOR_MODE_OFF]: '传感器按默认帧率运行',
  [SENSOR_MODE_FULLSPEED]: 'Full Speed · 提高传感器帧率',
  [SENSOR_MODE_FULLBLOOD]: 'Full Blood · 传感器满帧运行（FPS ≥ 20K）',
};

/** 设备回报的两个使能位 → 界面上的三选一（两个都开时以满血为准） */
const toSensorMode = (fullSpeedEnable, fullbloodEnable) => {
  if (fullbloodEnable) return SENSOR_MODE_FULLBLOOD;
  if (fullSpeedEnable) return SENSOR_MODE_FULLSPEED;
  return SENSOR_MODE_OFF;
};

/** 三选一 → 两个使能位，保证任何时候只有一个为 1 */
const sensorModeFlags = (mode) => ({
  fullSpeedEnable: mode === SENSOR_MODE_FULLSPEED ? 1 : 0,
  fullbloodEnable: mode === SENSOR_MODE_FULLBLOOD ? 1 : 0,
});

const LodDemo = ({ mm }) => (
  <div className="adv-lod-demo">
    <div
      className="adv-lod-actual"
      role="img"
      aria-label={`抬起高度示意：实际间隙 ${mm} 毫米`}
    >
      <div className="adv-lod-stage">
        <div className="adv-lod-threshold" />
        <div className="adv-lod-gap" style={{ height: `${mm}mm` }}>
          <span className="adv-lod-measure">{mm} mm</span>
        </div>
        <div className="adv-lod-pad" />
      </div>
    </div>
    <p className="adv-lod-demo-caption">
      抬离垫面超过 {mm} mm 即停止追踪 · 图示为实际尺寸
    </p>
  </div>
);

// 休眠时间：协议 type3 byte4-5(有线/2.4G) 与 byte6-7(蓝牙) 均为 uint16 秒，
// 界面合并成一个值，应用时自动写入两组字节。
const MIN_SLEEP_TIME = 10;
const MAX_SLEEP_TIME = 0xffff;
const clampSleepTime = (value) =>
  Math.max(
    MIN_SLEEP_TIME,
    Math.min(MAX_SLEEP_TIME, Math.round(Number(value) || 0))
  );

// 休眠时间档位：点击直接跳转，档位之间由进度条线性细分（仍可用输入框微调）
const SLEEP_STOPS = [
  { seconds: 10, label: '10秒' },
  { seconds: 30, label: '30秒' },
  { seconds: 60, label: '1分' },
  { seconds: 180, label: '3分' },
  { seconds: 300, label: '5分' },
  { seconds: 600, label: '10分' },
  { seconds: 1800, label: '30分' },
  { seconds: 3600, label: '1时' },
  { seconds: MAX_SLEEP_TIME, label: '最长' },
];
// 每个档位区间切成 100 小格，保证拖动时短时长段也有足够精度
const SLEEP_SEGMENT_STEPS = 100;
const SLEEP_SLIDER_MAX = (SLEEP_STOPS.length - 1) * SLEEP_SEGMENT_STEPS;

// 秒数 → 进度条位置（档位之间线性插值）
const sleepTimeToSlider = (seconds) => {
  const value = clampSleepTime(seconds);
  for (let i = 0; i < SLEEP_STOPS.length - 1; i += 1) {
    const from = SLEEP_STOPS[i].seconds;
    const to = SLEEP_STOPS[i + 1].seconds;
    if (value <= to) {
      const ratio = (value - from) / (to - from);
      return Math.round((i + ratio) * SLEEP_SEGMENT_STEPS);
    }
  }
  return SLEEP_SLIDER_MAX;
};

// 进度条位置 → 秒数
const sliderToSleepTime = (position) => {
  const pos = Math.max(0, Math.min(SLEEP_SLIDER_MAX, Number(position) || 0));
  const index = Math.min(
    SLEEP_STOPS.length - 2,
    Math.floor(pos / SLEEP_SEGMENT_STEPS)
  );
  const ratio = (pos - index * SLEEP_SEGMENT_STEPS) / SLEEP_SEGMENT_STEPS;
  const from = SLEEP_STOPS[index].seconds;
  const to = SLEEP_STOPS[index + 1].seconds;
  return clampSleepTime(from + ratio * (to - from));
};

// 322 → “5 分 22 秒”
const formatSleepTime = (seconds) => {
  const total = clampSleepTime(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  const parts = [];
  if (hours) parts.push(`${hours} 时`);
  if (minutes) parts.push(`${minutes} 分`);
  if (rest || !parts.length) parts.push(`${rest} 秒`);
  return parts.join(' ');
};

// 角度偏移：协议 sensor 参数 byte12，int8 有符号，-90° ~ +90°
const ANGLE_TUNABILITY_MIN = -90;
const ANGLE_TUNABILITY_MAX = 90;
// 档位间隔：拖动示意图/滑条时的刻度，仍可 1° 微调
const ANGLE_GEAR_STEP = 15;
const ANGLE_GEARS = [];
for (let deg = ANGLE_TUNABILITY_MIN; deg <= ANGLE_TUNABILITY_MAX; deg += ANGLE_GEAR_STEP) {
  ANGLE_GEARS.push(deg);
}
const clampAngleTunability = (value) =>
  Math.max(
    ANGLE_TUNABILITY_MIN,
    Math.min(ANGLE_TUNABILITY_MAX, Math.round(Number(value) || 0))
  );
// 有符号值写回单字节
const toInt8Byte = (value) => value & 0xff;
// 滑条填充百分比（-90 → 0%，0 → 50%，+90 → 100%）
const angleTunabilityPercent = (value) =>
  ((value - ANGLE_TUNABILITY_MIN) /
    (ANGLE_TUNABILITY_MAX - ANGLE_TUNABILITY_MIN)) *
  100;

const angleCaption = (value) =>
  value === 0 ? '无偏移' : value > 0 ? '轨迹右倾' : '轨迹左倾';

// 方向示意图：0° 竖直向上为基准，正值顺时针（轨迹右倾）
const ANGLE_DIAL_W = 208;
const ANGLE_DIAL_H = 124;
const ANGLE_DIAL_CX = 104;
const ANGLE_DIAL_CY = 104;
const ANGLE_DIAL_R = 78;
const ANGLE_DIAL_SWEEP_RADIUS = 44;
const ANGLE_LABEL_R = 93;

const polarPoint = (deg, radius) => ({
  x: ANGLE_DIAL_CX + radius * Math.sin((deg * Math.PI) / 180),
  y: ANGLE_DIAL_CY - radius * Math.cos((deg * Math.PI) / 180),
});

const angleSweepPath = (value) => {
  if (!value) return '';
  const start = polarPoint(0, ANGLE_DIAL_SWEEP_RADIUS);
  const end = polarPoint(value, ANGLE_DIAL_SWEEP_RADIUS);
  // |value| ≤ 90，永远是小弧；顺时针 sweep=1，逆时针 sweep=0
  const sweepFlag = value > 0 ? 1 : 0;
  return (
    `M ${ANGLE_DIAL_CX} ${ANGLE_DIAL_CY} L ${start.x.toFixed(2)} ${start.y.toFixed(2)} ` +
    `A ${ANGLE_DIAL_SWEEP_RADIUS} ${ANGLE_DIAL_SWEEP_RADIUS} 0 0 ${sweepFlag} ` +
    `${end.x.toFixed(2)} ${end.y.toFixed(2)} Z`
  );
};

const AngleDial = ({ value, onChange, disabled }) => {
  const svgRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  // 指针位置 → 角度：正上方为 0°，顺时针为正，超出半圆按 ±90 夹紧
  const angleFromPointer = useCallback((event) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    // viewBox 按 xMidYMid meet 等比缩放并居中，宽高比不一致时两侧会留白，
    // 直接按 rect 比例换算圆心会偏，必须扣掉留白
    const scale = Math.min(
      rect.width / ANGLE_DIAL_W,
      rect.height / ANGLE_DIAL_H
    );
    const cx =
      rect.left + (rect.width - ANGLE_DIAL_W * scale) / 2 + ANGLE_DIAL_CX * scale;
    const cy =
      rect.top + (rect.height - ANGLE_DIAL_H * scale) / 2 + ANGLE_DIAL_CY * scale;
    const dx = event.clientX - cx;
    const dy = event.clientY - cy;
    if (dx === 0 && dy === 0) return null;
    return clampAngleTunability((Math.atan2(dx, -dy) * 180) / Math.PI);
  }, []);

  const applyPointer = useCallback(
    (event) => {
      const next = angleFromPointer(event);
      if (next !== null) onChange(next);
    },
    [angleFromPointer, onChange]
  );

  const handlePointerDown = (event) => {
    if (disabled) return;
    event.preventDefault();
    if (event.currentTarget.setPointerCapture && event.pointerId !== undefined) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (e) {
        /* 部分浏览器/测试环境不支持捕获，忽略即可 */
      }
    }
    setDragging(true);
    applyPointer(event);
  };

  const handlePointerMove = (event) => {
    if (disabled || !dragging) return;
    event.preventDefault();
    applyPointer(event);
  };

  const stopDragging = (event) => {
    if (!dragging) return;
    if (
      event?.currentTarget?.releasePointerCapture &&
      event.pointerId !== undefined
    ) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (e) {
        /* 同上 */
      }
    }
    setDragging(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    const step = event.shiftKey ? 5 : 1;
    let next = null;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = value - step;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = value + step;
    else if (event.key === 'PageDown') next = value - ANGLE_GEAR_STEP;
    else if (event.key === 'PageUp') next = value + ANGLE_GEAR_STEP;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = ANGLE_TUNABILITY_MAX;
    if (next === null) return;
    event.preventDefault();
    onChange(clampAngleTunability(next));
  };

  const arcStart = polarPoint(ANGLE_TUNABILITY_MIN, ANGLE_DIAL_R);
  const arcEnd = polarPoint(ANGLE_TUNABILITY_MAX, ANGLE_DIAL_R);
  const knob = polarPoint(value, ANGLE_DIAL_R - 10);

  return (
    <svg
      ref={svgRef}
      className={'adv-angle-dial' + (dragging ? ' dragging' : '')}
      viewBox={`0 0 ${ANGLE_DIAL_W} ${ANGLE_DIAL_H}`}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label="角度偏移，可拖动旋转"
      aria-valuemin={ANGLE_TUNABILITY_MIN}
      aria-valuemax={ANGLE_TUNABILITY_MAX}
      aria-valuenow={value}
      aria-valuetext={`${value}°，${angleCaption(value)}`}
      aria-disabled={disabled || undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onLostPointerCapture={stopDragging}
      onKeyDown={handleKeyDown}
    >
      {/* 半圆底盘 */}
      <path
        className="adv-angle-dial-ring"
        d={
          `M ${arcStart.x.toFixed(2)} ${arcStart.y.toFixed(2)} ` +
          `A ${ANGLE_DIAL_R} ${ANGLE_DIAL_R} 0 0 1 ${arcEnd.x.toFixed(2)} ${arcEnd.y.toFixed(2)} Z`
        }
      />
      {value !== 0 && (
        <path className="adv-angle-dial-sweep" d={angleSweepPath(value)} />
      )}
      {/* 档位刻度与对应角度值 */}
      <g className="adv-angle-dial-gears">
        {ANGLE_GEARS.map((deg) => {
          const outer = polarPoint(deg, ANGLE_DIAL_R);
          const inner = polarPoint(deg, ANGLE_DIAL_R - (deg % 45 === 0 ? 12 : 7));
          const label = polarPoint(deg, ANGLE_LABEL_R);
          const active = value === deg;
          return (
            <g
              key={deg}
              className={
                'adv-angle-gear' +
                (deg % 45 === 0 ? ' major' : '') +
                (active ? ' active' : '')
              }
            >
              <line
                x1={inner.x.toFixed(2)}
                y1={inner.y.toFixed(2)}
                x2={outer.x.toFixed(2)}
                y2={outer.y.toFixed(2)}
              />
              <text
                x={label.x.toFixed(2)}
                y={(label.y + 3).toFixed(2)}
                textAnchor="middle"
              >
                {deg > 0 ? `+${deg}` : deg}
              </text>
            </g>
          );
        })}
      </g>
      {/* 0° 基准线：鼠标实际推动方向 */}
      <line
        className="adv-angle-dial-base"
        x1={ANGLE_DIAL_CX}
        y1={ANGLE_DIAL_CY}
        x2={ANGLE_DIAL_CX}
        y2={ANGLE_DIAL_CY - ANGLE_DIAL_R}
      />
      {/* 偏移后的方向：由扇形与旋钮表示，可直接拖动 */}
      <line
        className="adv-angle-dial-lead"
        x1={ANGLE_DIAL_CX}
        y1={ANGLE_DIAL_CY}
        x2={knob.x.toFixed(2)}
        y2={knob.y.toFixed(2)}
      />
      <circle
        className="adv-angle-dial-knob"
        cx={knob.x.toFixed(2)}
        cy={knob.y.toFixed(2)}
        r="7"
      />
      <circle
        className="adv-angle-dial-hub"
        cx={ANGLE_DIAL_CX}
        cy={ANGLE_DIAL_CY}
        r="4"
      />
    </svg>
  );
};

const Toggle = ({ on, onChange, disabled }) => (
  <button
    type="button"
    className={`adv-toggle ${on ? 'on' : ''}`}
    onClick={() => !disabled && onChange(!on)}
    disabled={disabled}
    aria-pressed={on}
  >
    <span className="adv-toggle-knob" />
  </button>
);

const AdvancedSettings = () => {
  const { sender, dataStore, isConnected } = useDevice();
  const [busy, setBusy] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  const [lod, setLod] = useState(1);
  const [sensorType, setSensorType] = useState(SENSOR_TYPE_3950);
  const [rippleEnable, setRippleEnable] = useState(0);
  const [angleSnapEnable, setAngleSnapEnable] = useState(0);
  const [motionSyncEnable, setMotionSyncEnable] = useState(0);
  // 传感器性能模式：三选一，见 SENSOR_MODE_OPTIONS
  const [sensorMode, setSensorMode] = useState(SENSOR_MODE_OFF);
  const [angleTunability, setAngleTunability] = useState(0);
  const [wheelReverseEnable, setWheelReverseEnable] = useState(0);
  const [fastButtonEnable, setFastButtonEnable] = useState(0);
  const [leftDebouncetimePress, setLeftDebouncetimePress] = useState(0);
  // 输入过程中保留原始文本，失焦时再夹紧到 10–65535
  const [sleepTimeInput, setSleepTimeInput] = useState(String(MIN_SLEEP_TIME));
  const sleepTime = clampSleepTime(sleepTimeInput);
  const sleepSliderValue = sleepTimeToSlider(sleepTime);
  const lodOptions = useMemo(() => getLodOptions(sensorType), [sensorType]);
  const activeLodMm = getLodMillimeters(sensorType, lod);

  // 表单快照：任何一个用户可改的值变化 + markDirty 后触发自动写入
  const settingsSnapshot = useMemo(
    () => ({
      lod,
      rippleEnable,
      angleSnapEnable,
      motionSyncEnable,
      sensorMode,
      angleTunability,
      wheelReverseEnable,
      fastButtonEnable,
      leftDebouncetimePress,
      sleepTime,
    }),
    [
      lod,
      rippleEnable,
      angleSnapEnable,
      motionSyncEnable,
      sensorMode,
      angleTunability,
      wheelReverseEnable,
      fastButtonEnable,
      leftDebouncetimePress,
      sleepTime,
    ]
  );

  /** 把当前界面值写入设备；由 useAutoApply 在用户停手后调用 */
  const applyToDevice = async () => {
    if (!sender) throw new Error('设备未连接');
    await sendType1();
    await sender.wait(80);
    await sendType3();
    await sender.wait(80);
    dataStore.emit('performanceData', dataStore.data.performanceData);
    await sender.getPerformanceData(1);
    await sender.wait(40);
    await sender.getPerformanceData(3);
  };

  const { saveStatus, markDirty, ownedByUser, release } = useAutoApply({
    value: settingsSnapshot,
    apply: applyToDevice,
    enabled: isConnected && !!sender,
    isConnected,
    onError: (error) => toast.error('应用失败: ' + (error.message || error)),
  });

  // 用户改动入口：先标脏再改值；设备回读走原 setter，不经过这里
  const edit = (setter) => (value) => {
    markDirty();
    setter(value);
  };
  const editLod = edit(setLod);
  const editSensorMode = edit(setSensorMode);
  const editAngleTunability = edit(setAngleTunability);
  const editWheelReverseEnable = edit(setWheelReverseEnable);
  const editFastButtonEnable = edit(setFastButtonEnable);
  const editLeftDebouncetimePress = edit(setLeftDebouncetimePress);
  const editSleepTimeInput = edit(setSleepTimeInput);

  useEffect(() => {
    if (!dataStore) return;
    const apply = (data) => {
      // 用户动过界面之后表单以界面为准，设备回读不再回灌
      if (ownedByUser()) return;
      if (data?.type1?.parsed) {
        const p = data.type1.parsed;
        if (p.liftoffHeight !== undefined) setLod(p.liftoffHeight);
        if (p.rippleEnable !== undefined) setRippleEnable(p.rippleEnable);
        if (p.angleSnapEnable !== undefined) setAngleSnapEnable(p.angleSnapEnable);
        if (p.motionSyncEnable !== undefined) setMotionSyncEnable(p.motionSyncEnable);
        if (p.fullSpeedEnable !== undefined || p.fullbloodEnable !== undefined) {
          setSensorMode(toSensorMode(p.fullSpeedEnable, p.fullbloodEnable));
        }
        if (p.angleTunability !== undefined) {
          setAngleTunability(clampAngleTunability(p.angleTunability));
        }
      }
      if (data?.type3?.parsed) {
        const p = data.type3.parsed;
        const wheelReverseValue = p.wheelReverseEnable;
        const fastButtonValue = p.fastButtonEnable;
        const debounceValue = p.leftDebouncetimePress;
        if (wheelReverseValue !== undefined) {
          setWheelReverseEnable(wheelReverseValue);
        }
        if (fastButtonValue !== undefined) setFastButtonEnable(fastButtonValue);
        if (debounceValue !== undefined) {
          setLeftDebouncetimePress(debounceValue);
        }
        // 两组字节合并展示：取设备上已有的值（优先有线/2.4G）
        const deviceSleepTime = p.SysSleepTime ?? p.bleSysSleepTime;
        if (deviceSleepTime !== undefined) {
          setSleepTimeInput(String(clampSleepTime(deviceSleepTime)));
        }
      }
    };
    const applyInfo = (data) => {
      const parsed = data?.parsed;
      if (!parsed) return;
      // 传感器型号是硬件信息，始终跟随设备
      if (parsed.sensorType !== undefined && parsed.sensorType !== null) {
        setSensorType(parsed.sensorType);
      }
      const flagLod = extractLodFromSensorFlag(parsed.sensorFlag);
      if (flagLod !== null && !ownedByUser()) setLod(flagLod);
    };
    const initial = dataStore.getData('performanceData');
    if (initial) apply(initial);
    const initialInfo = dataStore.getData('informationData');
    if (initialInfo) applyInfo(initialInfo);
    dataStore.on('performanceData', apply);
    dataStore.on('informationData', applyInfo);
    return () => {
      dataStore.off('performanceData', apply);
      dataStore.off('informationData', applyInfo);
    };
  }, [dataStore, ownedByUser]);

  const ensurePerf = async (type) => {
    const key = type === 1 ? 'type1' : 'type3';
    let raw = dataStore?.getData('performanceData')?.[key]?.raw;
    if (raw?.length >= 14) return raw;
    if (!sender) throw new Error('设备未连接');
    await sender.getPerformanceData(type);
    for (let i = 0; i < 8; i++) {
      await sender.wait(40);
      raw = dataStore?.getData('performanceData')?.[key]?.raw;
      if (raw?.length >= 14) return raw;
    }
    throw new Error('获取性能数据失败，请重试');
  };

  const sendType1 = async () => {
    const type1Raw = await ensurePerf(1);
    const packet = new Uint8Array(64);
    packet.set(type1Raw.subarray(0, Math.min(type1Raw.length, 64)));
    packet[3] = 0x02;
    packet[4] = angleSnapEnable ? 1 : 0;
    packet[5] = rippleEnable ? 1 : 0;
    packet[6] = motionSyncEnable ? 1 : 0;
    // 字段 3/4 在 4.0 与 4.0 以下的 type1 布局里都落在 byte7/byte8，互斥下发
    const modeFlags = sensorModeFlags(sensorMode);
    packet[7] = modeFlags.fullSpeedEnable;
    packet[8] = modeFlags.fullbloodEnable;
    const lodWrite = normalizeLodWriteValue(sensorType, lod);
    packet[11] = lodWrite;
    packet[12] = toInt8Byte(clampAngleTunability(angleTunability));
    await sender.sendOutputReport(0, packet);
    if (sender.sendConfirmation) await sender.sendConfirmation();
    const perf = dataStore.getData('performanceData');
    if (perf?.type1) {
      dataStore.data.performanceData.type1 = {
        raw: packet,
        parsed: {
          ...perf.type1.parsed,
          angleSnapEnable: packet[4],
          rippleEnable: packet[5],
          motionSyncEnable: packet[6],
          fullSpeedEnable: packet[7],
          fullbloodEnable: packet[8],
          liftoffHeight: lodWrite,
          angleTunability: clampAngleTunability(angleTunability),
        },
        timestamp: Date.now(),
      };
    }
  };

  const sendType3 = async () => {
    const type3Raw = await ensurePerf(3);
    // 统一值由 encodeSleepTimes 自动写入有线/2.4G 与蓝牙两组字节
    const packet = sender.encodeSleepTimes({ sleepTime }, type3Raw);
    const isV4 = dataStore.isProtocolAtLeast(4, 0);
    // 4.0 的按键/滚轮字段偏移统一由 ButtonPerformance 维护
    const debounceOffset = isV4
      ? V4_TYPE3_OFFSETS.leftDebouncetimePress
      : 9;
    const fastButtonOffset = isV4
      ? V4_TYPE3_OFFSETS.fastButtonEnable
      : 10;
    const wheelReverseOffset = isV4
      ? V4_TYPE3_OFFSETS.wheelReverseEnable
      : 14;
    packet[debounceOffset] = leftDebouncetimePress;
    packet[fastButtonOffset] = fastButtonEnable ? 1 : 0;
    packet[wheelReverseOffset] = wheelReverseEnable ? 1 : 0;
    await sender.sendOutputReport(0, packet);
    if (sender.sendConfirmation) await sender.sendConfirmation();
    const perf = dataStore.getData('performanceData');
    if (perf?.type3) {
      dataStore.data.performanceData.type3 = {
        raw: packet,
        parsed: {
          ...perf.type3.parsed,
          leftDebouncetimePress,
          fastButtonEnable: packet[fastButtonOffset],
          wheelReverseEnable: packet[wheelReverseOffset],
          SysSleepTime: sleepTime,
          bleSysSleepTime: sleepTime,
        },
        timestamp: Date.now(),
      };
    }
  };

  const handleFactoryReset = async () => {
    if (!isConnected || !sender) {
      toast.error('设备未连接');
      return;
    }
    setBusy(true);
    try {
      await sender.factoryReset();
      // 出厂后设备数据全部重读：把表单交还给设备回读，丢弃未写入的改动
      release();
      setShowResetModal(false);
      toast.success('已发送恢复出厂命令，正在重新读取…');
      await sender.wait(500);
      await sender.getDeviceConfig();
      await sender.wait(100);
      await sender.getInformationData();
      await sender.wait(100);
      await sender.getMouseButtonData();
      await sender.wait(100);
      await sender.getPerformanceData(1);
      await sender.wait(100);
      await sender.getPerformanceData(3);
      await sender.wait(100);
      await sender.getMacroData(1);
      toast.success('设备数据已刷新');
    } catch (e) {
      console.error(e);
      toast.error('恢复出厂失败: ' + (e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const sensorRows = useMemo(
    () => [
      {
        key: 'rippleEnable',
        title: '波纹控制',
        hint: 'Ripple Control · 抑制高速涟漪',
        on: !!rippleEnable,
        set: (v) => {
          markDirty();
          setRippleEnable(v ? 1 : 0);
        },
      },
      {
        key: 'angle',
        title: '直线修正',
        hint: 'Angle Snapping · 强制直线轨迹',
        on: !!angleSnapEnable,
        set: (v) => {
          markDirty();
          setAngleSnapEnable(v ? 1 : 0);
        },
      },
      {
        key: 'msync',
        title: 'Motion Sync',
        hint: '动作同步 · 对齐传感器采样',
        on: !!motionSyncEnable,
        set: (v) => {
          markDirty();
          setMotionSyncEnable(v ? 1 : 0);
        },
      },
    ],
    [rippleEnable, angleSnapEnable, motionSyncEnable, markDirty]
  );

  // 头部状态行：未连接优先于保存状态
  const status = resolveAutoApplyStatus({
    isConnected,
    ready: true,
    saveStatus,
  });

  return (
    <div className="adv-page">
      <div className="adv-scroll">
        <header className="adv-header">
          <div>
            <div className="adv-eyebrow">设备设置</div>
            <h1 className="adv-title">鼠标设置</h1>
            <p className="adv-subtitle">传感器、滚轮与按键响应参数</p>
          </div>
          <span
            className={`adv-save-status is-${status.kind}`}
            role="status"
            aria-live="polite"
          >
            {status.text}
          </span>
        </header>
        <section className="adv-card">
          <div className="adv-card-head">
            <h2>抬起高度 (LOD)</h2>
            <span className="adv-card-tag">传感器</span>
          </div>
          <p className="adv-card-desc">鼠标抬离垫面后停止追踪的距离</p>
          <div className="adv-lod-body">
            <div
              className={
                'adv-seg' + (lodOptions.length > 3 ? ' adv-seg-many' : '')
              }
            >
              {lodOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={
                    'adv-seg-item' + (isLodOptionActive(opt, lod) ? ' active' : '')
                  }
                  onClick={() => editLod(opt.value)}
                >
                  <span className="adv-seg-label">{opt.label}</span>
                  {opt.desc ? (
                    <span className="adv-seg-desc">{opt.desc}</span>
                  ) : null}
                </button>
              ))}
            </div>
            <LodDemo mm={activeLodMm} />
          </div>
        </section>

        <section className="adv-card">
          <div className="adv-card-head">
            <h2>画线性能</h2>
            <span className="adv-card-tag">传感器</span>
          </div>
          <div className="adv-rows">
            {sensorRows.map((row) => (
              <div className="adv-row" key={row.key}>
                <div className="adv-row-text">
                  <div className="adv-row-title">{row.title}</div>
                  <div className="adv-row-hint">{row.hint}</div>
                </div>
                <Toggle on={row.on} onChange={row.set} disabled={!isConnected} />
              </div>
            ))}
            <div className="adv-row">
              <div className="adv-row-text">
                <div className="adv-row-title">性能模式</div>
                <div className="adv-row-hint">
                  {SENSOR_MODE_DESC[sensorMode]} · 两种模式互斥，有线与 2.4G 生效
                </div>
              </div>
              <div className="adv-chip-group">
                {SENSOR_MODE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={
                      'adv-chip' + (sensorMode === opt.value ? ' active' : '')
                    }
                    onClick={() => editSensorMode(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="adv-row adv-row-stack">
              <div className="adv-row-head">
                <div className="adv-row-text">
                  <div className="adv-row-title">角度偏移</div>
                  <div className="adv-row-hint">
                    Angle Tunability · 补偿握姿导致的轨迹倾斜，可直接拖动示意图旋转
                  </div>
                </div>
                <button
                  type="button"
                  className="adv-chip adv-chip-ghost"
                  onClick={() => editAngleTunability(0)}
                  disabled={!isConnected || angleTunability === 0}
                >
                  归零
                </button>
              </div>
              <div className="adv-angle-body">
                <div className="adv-angle-preview">
                  <AngleDial
                    value={angleTunability}
                    onChange={editAngleTunability}
                    disabled={!isConnected}
                  />
                  <span className="adv-angle-caption">
                    拖动旋转 · {angleCaption(angleTunability)}
                  </span>
                </div>
                <div className="adv-slider-block adv-angle-slider-block">
                  <input
                    type="range"
                    min={ANGLE_TUNABILITY_MIN}
                    max={ANGLE_TUNABILITY_MAX}
                    step={1}
                    value={angleTunability}
                    onChange={(e) =>
                      editAngleTunability(clampAngleTunability(e.target.value))
                    }
                    disabled={!isConnected}
                    className="adv-slider adv-slider-bipolar"
                    style={{
                      '--pct': angleTunabilityPercent(angleTunability) + '%',
                    }}
                    aria-label="角度偏移"
                  />
                  <div className="adv-slider-meta">
                    <span>{ANGLE_TUNABILITY_MIN}°（左倾）</span>
                    <span className="adv-slider-val">
                      {angleTunability > 0
                        ? `+${angleTunability}`
                        : angleTunability}
                      °
                    </span>
                    <span>+{ANGLE_TUNABILITY_MAX}°（右倾）</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="adv-card">
          <div className="adv-card-head">
            <h2>滚轮与按键</h2>
            <span className="adv-card-tag">系统</span>
          </div>
          <div className="adv-rows">
            <div className="adv-row">
              <div className="adv-row-text">
                <div className="adv-row-title">滚轮方向</div>
                <div className="adv-row-hint">反向后滚动方向与系统默认相反</div>
              </div>
              <div className="adv-chip-group">
                <button
                  type="button"
                  className={'adv-chip' + (wheelReverseEnable === 0 ? ' active' : '')}
                  onClick={() => editWheelReverseEnable(0)}
                >
                  正向
                </button>
                <button
                  type="button"
                  className={'adv-chip' + (wheelReverseEnable === 1 ? ' active' : '')}
                  onClick={() => editWheelReverseEnable(1)}
                >
                  反向
                </button>
              </div>
            </div>
            <div className="adv-row">
              <div className="adv-row-text">
                <div className="adv-row-title">按键快速响应</div>
                <div className="adv-row-hint">降低点击延迟，竞技场景可开启</div>
              </div>
              <Toggle
                on={!!fastButtonEnable}
                onChange={(v) => editFastButtonEnable(v ? 1 : 0)}
                disabled={!isConnected}
              />
            </div>
            <div className="adv-row adv-row-stack">
              <div className="adv-row-text">
                <div className="adv-row-title">按键防抖</div>
                <div className="adv-row-hint">数值越高越不易连点，响应稍慢</div>
              </div>
              <div className="adv-slider-block">
                <input
                  type="range"
                  min={0}
                  max={20}
                  step={1}
                  value={leftDebouncetimePress}
                  onChange={(e) =>
                    editLeftDebouncetimePress(Number(e.target.value))
                  }
                  className="adv-slider"
                  style={{ '--pct': (leftDebouncetimePress / 20) * 100 + '%' }}
                />
                <div className="adv-slider-meta">
                  <span>0 ms</span>
                  <span className="adv-slider-val">
                    {leftDebouncetimePress} ms
                  </span>
                  <span>20 ms</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="adv-card">
          <div className="adv-card-head">
            <h2>休眠时间</h2>
            <span className="adv-card-tag">电源管理</span>
          </div>
          <div className="adv-rows">
            <div className="adv-row adv-row-stack">
              <div className="adv-row-head">
                <div className="adv-row-text">
                  <div className="adv-row-title">无操作等待时间</div>
                  <div className="adv-row-hint">
                    超过该时间未操作即进入休眠，范围 {MIN_SLEEP_TIME}–
                    {MAX_SLEEP_TIME} 秒
                  </div>
                </div>
                <label className="adv-number-control adv-sleep-control">
                  <input
                    type="number"
                    min={MIN_SLEEP_TIME}
                    max={MAX_SLEEP_TIME}
                    step={1}
                    value={sleepTimeInput}
                    onChange={(e) =>
                      editSleepTimeInput(e.target.value.replace(/[^\d]/g, ''))
                    }
                    onBlur={() => setSleepTimeInput(String(sleepTime))}
                    disabled={!isConnected || busy}
                    aria-label="休眠时间（秒）"
                  />
                  <span>秒</span>
                </label>
              </div>
              <div className="adv-slider-block adv-sleep-slider-block">
                <input
                  type="range"
                  min={0}
                  max={SLEEP_SLIDER_MAX}
                  step={1}
                  value={sleepSliderValue}
                  onChange={(e) =>
                    editSleepTimeInput(String(sliderToSleepTime(e.target.value)))
                  }
                  disabled={!isConnected || busy}
                  className="adv-slider adv-sleep-slider"
                  style={{
                    '--pct': (sleepSliderValue / SLEEP_SLIDER_MAX) * 100 + '%',
                  }}
                  aria-label="休眠时间进度条"
                  aria-valuetext={formatSleepTime(sleepTime)}
                />
                {/* 档位：点击直接跳转，跳转后仍可用上方输入框微调 */}
                <div className="adv-sleep-stops">
                  {SLEEP_STOPS.map((stop, index) => (
                    <button
                      key={stop.seconds}
                      type="button"
                      className={
                        'adv-sleep-stop' +
                        (sleepTime === stop.seconds ? ' active' : '')
                      }
                      style={{
                        left: (index / (SLEEP_STOPS.length - 1)) * 100 + '%',
                      }}
                      onClick={() => editSleepTimeInput(String(stop.seconds))}
                      disabled={!isConnected || busy}
                      title={`${stop.seconds} 秒`}
                    >
                      <span className="adv-sleep-stop-tick" />
                      <span className="adv-sleep-stop-label">{stop.label}</span>
                    </button>
                  ))}
                </div>
                <div className="adv-slider-meta adv-sleep-meta">
                  <span>当前</span>
                  <span className="adv-slider-val">
                    {formatSleepTime(sleepTime)}
                  </span>
                  <span>{sleepTime} 秒</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="adv-card adv-card-danger">
          <div className="adv-card-head">
            <h2>恢复出厂</h2>
            <span className="adv-card-tag danger">危险</span>
          </div>
          <p className="adv-card-desc">
            将清除 DPI、按键映射、宏等全部自定义配置，且无法撤销。
          </p>
          <button
            type="button"
            className="adv-danger-btn"
            onClick={() => setShowResetModal(true)}
            disabled={!isConnected || busy}
          >
            恢复出厂设置…
          </button>
        </section>
      </div>

      {showResetModal && (
        <div
          className="adv-modal-overlay"
          onClick={() => !busy && setShowResetModal(false)}
        >
          <div className="adv-modal" onClick={(e) => e.stopPropagation()}>
            <h3>确认恢复出厂设置？</h3>
            <p>设备上的个性化配置将被清空，需重新设置。</p>
            <div className="adv-modal-actions">
              <button
                type="button"
                className="adv-modal-cancel"
                onClick={() => setShowResetModal(false)}
                disabled={busy}
              >
                取消
              </button>
              <button
                type="button"
                className="adv-modal-confirm"
                onClick={handleFactoryReset}
                disabled={busy}
              >
                {busy ? '执行中…' : '确认恢复'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSettings;
